import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import {httpsCallable} from 'firebase/functions';
import {createBrowserExtensionErrorGate} from '@cip/core/utils';
import {functions} from './firebase';
import App from './App';
import './index.css';

if (import.meta.env.PROD) {
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
  console.trace = () => {};
  console.table = () => {};

  const logError = httpsCallable(functions, 'logError');
  const logWarning = httpsCallable(functions, 'logWarning');

  const serializeForLog = (args: unknown[]): unknown[] => {
    return args.map(item => {
      if (Array.isArray(item)) {
        return serializeForLog(item);
      }
      if (item && typeof item === 'object') {
        // Extract non-enumerable properties (like message/stack from Error objects)
        // while preserving all other properties and avoiding issues with strings/null
        // do this because Route ErrorResponse objects are not serializable by default
        return {
          ...item,
          ...('message' in item ? {message: item.message} : {}),
          ...('stack' in item ? {stack: item.stack} : {}),
          ...('name' in item ? {name: item.name} : {}),
        };
      }
      return item;
    });
  };

  // Extensions inject scripts into the page, so their throws reach our window
  // error handler and bill us one logError invocation each. Report the first
  // throw per extension per session and drop the rest. Refs #3127.
  const extensionErrors = createBrowserExtensionErrorGate();

  console.warn = (...logged) => {
    void logWarning({
      logged: serializeForLog(logged),
      url: window.location.href,
      userAgent: navigator.userAgent,
      webApp: 'teacher-feedback',
    });
  };

  console.error = (...logged) => {
    const serialized = serializeForLog(logged);
    if (extensionErrors.shouldDropLoggedArgs(serialized)) {
      return;
    }
    void logError({
      logged: serialized,
      url: window.location.href,
      userAgent: navigator.userAgent,
      webApp: 'teacher-feedback',
    });
  };

  const reportError = (error: unknown, filename?: string) => {
    const isError = error instanceof Error;
    const stack = isError ? error.stack : undefined;
    if (extensionErrors.shouldDrop({stack, filename})) {
      return;
    }
    void logError({
      logged: {
        message: isError ? error.message : String(error),
        stack,
        name: isError ? error.name : undefined,
      },
      url: window.location.href,
      userAgent: navigator.userAgent,
      webApp: 'teacher-feedback',
    });
  };

  window.addEventListener('error', event => {
    // `event.filename` is the only attribution available for a cross-origin
    // script, where the browser withholds `event.error` and its stack.
    reportError(event.error ?? event.message, event.filename);
  });

  window.addEventListener('unhandledrejection', event => {
    reportError(event.reason ?? 'unhandledrejection');
  });
}

const rootElement = document.getElementById('root');
if (rootElement && !rootElement.innerHTML) {
  createRoot(rootElement).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}
